import React from 'react'
import {
    isAndroid,
    isIOS,
    isMobileOnly,
    isMobileSafari,
    isSafari,
} from 'react-device-detect'

import { userTypes } from 'helpers/user'
import { copyTextToClipboard } from 'utils/utils'

import Button, { btnTypes } from '../../common/Button'
import Heading from '../../common/Heading'
import { GET_CHROME_URL, INSTALL_CHROME_URL } from '../constant'
import {
    ImagesContainer,
    MobileImage,
    IpadImage,
    LaptopImage,
    LinkTextSmall,
    NetworkWrapper,
    CenteredAlign,
    ChromeButton,
    ChromeContainer,
    CopyTextInput,
    ImageCopy,
    InstallChrome,
    LeftTextWithImageDiv,
    List,
    ListLi,
    BrowserWrapper,
    Maintext,
    ParticipantNotAllowedWrap,
    Title,
    Info,
    Icon,
    Text,
    CtaTitle,
    CtaSection,
    CtaBtns,
    BtnWithSubText,
} from '../styles'
import {
    AndroidTroubleshoot,
    AndroidGif,
    AndroidGifWrapper,
    Instructions,
    Step,
    SafariTroubleshoot,
    SafariGifWrapper,
    SafariGif,
    DesktopTroubleshoot,
    DesktopGifWrapper,
    DesktopGif,
    TroubleshootList,
    TroubleshootListIem,
} from '../styles/av'

const reloadWebsite = (text = 'click here') => (
    <LinkTextSmall onClick={() => window.location.reload()}>
        {text}
    </LinkTextSmall>
)

const renderIcon = (icon, alt) => (
    <img src={`/img/checklist/${icon}.svg`} alt={alt} />
)

const Network = (
    <NetworkWrapper>
        <List>
            {renderIcon('network-error', 'Network')}
            <ListLi>{reloadWebsite()} to refresh this page.</ListLi>
        </List>
    </NetworkWrapper>
)

const Device = userType => {
    const showIcon = userType === userTypes.STUDENT
    const width = showIcon ? 68 : 50
    return (
        <ImagesContainer width={width}>
            <LaptopImage />
            {showIcon && (
                <>
                    <MobileImage />
                    <IpadImage />
                </>
            )}
        </ImagesContainer>
    )
}

const AudioVideo = props => {
    const IMG_BASE = '/img/checklist/gifs'
    const isSafariIOS = isSafari || isMobileSafari
    const isChromeAndroid = isMobileOnly && isAndroid
    const { isLandscape, isPortrait } = props

    const androidGif = (
        <AndroidGifWrapper>
            <AndroidGif src={`${IMG_BASE}/chrome.android.phone.gif`} />
        </AndroidGifWrapper>
    )

    const safariGif = (
        <SafariGifWrapper>
            <SafariGif src={`${IMG_BASE}/safari.ipad.gif`} />
        </SafariGifWrapper>
    )

    const step1 = (
        <Step>
            <b>step 1: </b>grant audio and video permission
        </Step>
    )

    const step2 = (
        <Step>
            <b>step 2: </b> {reloadWebsite()} to refresh tab
        </Step>
    )

    if (isChromeAndroid && isPortrait)
        return (
            <AndroidTroubleshoot>
                <Heading {...props} />
                {step1}
                {androidGif}
                {step2}
            </AndroidTroubleshoot>
        )

    if (isChromeAndroid && isLandscape)
        return (
            <AndroidTroubleshoot>
                {androidGif}
                <Instructions>
                    <Heading {...props} />
                    {step1}
                    {step2}
                </Instructions>
            </AndroidTroubleshoot>
        )

    if (isSafariIOS) {
        return (
            <SafariTroubleshoot>
                <Instructions>
                    <Heading {...props} />
                    <Step>
                        <b>step 1: </b> {reloadWebsite()} to refresh tab
                    </Step>
                    <Step>
                        <b>step 2: </b>grant audio and video permission
                    </Step>
                </Instructions>
                {safariGif}
            </SafariTroubleshoot>
        )
    }

    return (
        <DesktopTroubleshoot>
            <Heading {...props} subText="" />
            {step1}
            <DesktopGifWrapper>
                <DesktopGif src={`${IMG_BASE}/chrome.desktop.gif`} />
            </DesktopGifWrapper>
            {step2}
        </DesktopTroubleshoot>
    )
}

const Browser = (
    <BrowserWrapper>
        <Maintext>
            <p>open {isIOS ? 'safari' : 'chrome'} and paste this link.</p>
        </Maintext>
        <CenteredAlign>
            <CopyTextInput>{window.location.href}</CopyTextInput>
            <ImageCopy
                src="/img/checklist/copy.svg"
                alt="copy"
                onClick={() => copyTextToClipboard(window.location.href)}
            />
        </CenteredAlign>
        {!isIOS && (
            <>
                <LeftTextWithImageDiv>
                    <p>if you do not have Chrome, </p>
                </LeftTextWithImageDiv>
                <LeftTextWithImageDiv>
                    <ChromeContainer>
                        <ChromeButton
                            onClick={() =>
                                window.open(GET_CHROME_URL, '_blank')
                            }
                        >
                            {renderIcon('chrome', 'Chrome')}
                            download Chrome
                        </ChromeButton>
                        <InstallChrome
                            href={INSTALL_CHROME_URL}
                            target="_blank"
                        >
                            how to install Chrome?
                        </InstallChrome>
                    </ChromeContainer>
                </LeftTextWithImageDiv>
            </>
        )}
    </BrowserWrapper>
)

const ParticipantNotAllowed = ({ title, infoCopy, ctaTitle, buttonProps }) => {
    const btnProps = {
        ...buttonProps,
        onClick: () => window.open(buttonProps.path, '_blank'),
    }
    return (
        <ParticipantNotAllowedWrap>
            <Title>{title}</Title>
            <Info>
                <Icon />
                <Text>{infoCopy}</Text>
            </Info>
            <CtaTitle>{ctaTitle}</CtaTitle>
            <CtaSection>
                <CtaBtns>
                    <BtnWithSubText>
                        <Button {...btnProps} />
                    </BtnWithSubText>
                </CtaBtns>
            </CtaSection>
        </ParticipantNotAllowedWrap>
    )
}

export const AudioVideo404 = () => {
    const steps = [
        <>
            try closing other video apps/websites and {reloadWebsite('refresh')}
        </>,
        'try restarting your browser',
        'try restarting your device',
        'if none of the above work, please reach out to us',
    ]
    return (
        <TroubleshootList>
            {steps.map((step, index) => (
                <TroubleshootListIem key={index}>{step}</TroubleshootListIem>
            ))}
        </TroubleshootList>
    )
}

export { Network, AudioVideo, Browser, Device, ParticipantNotAllowed }
